package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.ui.graphics.Color
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.Edit
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminPermissionAction
import org.gymly.model.AdminPermissionCapabilityGroup
import org.gymly.model.AdminRolePermissionTemplate
import org.gymly.model.AdminSettingsPermissionModule
import org.gymly.model.AdminSettingsRole
import org.gymly.model.AdminSettingsRoleUpsert
import org.gymly.theme.AdminColors
import org.gymly.util.userMessage
import org.gymly.ui.components.VerticalScrollColumn

private sealed class RoleDialog {
    data object Create : RoleDialog()
    data class Edit(val item: AdminSettingsRole) : RoleDialog()
    data class Detail(val item: AdminSettingsRole) : RoleDialog()
    data class Delete(val item: AdminSettingsRole) : RoleDialog()
}

@Composable
internal fun SettingsRolesPanel(compact: Boolean) {
    val scope = rememberCoroutineScope()
    var items by remember { mutableStateOf<List<AdminSettingsRole>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var query by remember { mutableStateOf("") }
    var dialog by remember { mutableStateOf<RoleDialog?>(null) }
    var saving by remember { mutableStateOf(false) }

    fun reload() {
        scope.launch {
            loading = true
            error = null
            try {
                items = AdminRepo.api.listSettingsRoles()
            } catch (e: Exception) {
                error = e.userMessage("No se pudieron cargar los roles.")
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(Unit) { reload() }

    val filtered = remember(items, query) {
        val q = query.trim().lowercase()
        if (q.isEmpty()) items
        else items.filter { role ->
            role.name.lowercase().contains(q) ||
                AdminPermissionCapabilityGroup.entries.any { g ->
                    g.labelEs().lowercase().contains(q) &&
                        g.actions().any { a -> g.permission(a)?.let { role.has(it) } == true }
                }
        }
    }

    val listPaging = rememberAdminListPaging(initialSortKey = "name", resetKey = query)
    val pageItems = remember(filtered, listPaging.sort, listPaging.page) {
        filtered.adminSortedPaged(listPaging.sort, listPaging.page) { _ ->
            { r -> r.name.lowercase() }
        }
    }

    Column(Modifier.fillMaxSize()) {
        error?.let {
            Text(it, color = AdminColors.Error, fontSize = 13.sp, modifier = Modifier.padding(bottom = 8.dp))
        }
        RolesDirectoryCard(
            roles = pageItems,
            totalCount = filtered.size,
            listPaging = listPaging,
            loading = loading,
            query = query,
            onQueryChange = { query = it },
            compact = compact,
            onCreate = { dialog = RoleDialog.Create },
            onView = { dialog = RoleDialog.Detail(it) },
            onEdit = { dialog = RoleDialog.Edit(it) },
            onDelete = { dialog = RoleDialog.Delete(it) },
        )
    }

    when (val d = dialog) {
        is RoleDialog.Detail -> RoleDetailDialog(d.item) { dialog = null }
        is RoleDialog.Create -> RoleFormDialog(
            title = "Agregar rol",
            initial = null,
            saving = saving,
            serverError = error,
            onDismiss = { if (!saving) dialog = null },
            onSave = { upsert ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        AdminRepo.api.createSettingsRole(upsert)
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        error = e.userMessage("No se pudo crear el rol.")
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is RoleDialog.Edit -> RoleFormDialog(
            title = "Editar rol",
            initial = d.item,
            saving = saving,
            serverError = error,
            onDismiss = { if (!saving) dialog = null },
            onSave = { upsert ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        AdminRepo.api.updateSettingsRole(d.item.id, upsert)
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        error = e.userMessage("No se pudo guardar el rol.")
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is RoleDialog.Delete -> AlertDialog(
            onDismissRequest = { if (!saving) dialog = null },
            title = { Text("Eliminar rol") },
            text = {
                Text("¿Seguro que deseas eliminar el rol ${d.item.name}? Esta acción no se puede deshacer.")
            },
            confirmButton = {
                Button(
                    onClick = {
                        scope.launch {
                            saving = true
                            error = null
                            try {
                                AdminRepo.api.deleteSettingsRole(d.item.id)
                                dialog = null
                                reload()
                            } catch (e: Exception) {
                                error = e.userMessage("No se pudo eliminar el rol.")
                            } finally {
                                saving = false
                            }
                        }
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else "Eliminar")
                }
            },
            dismissButton = {
                TextButton(onClick = { dialog = null }, enabled = !saving) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
            },
        )
        null -> Unit
    }
}

@Composable
private fun ColumnScope.RolesDirectoryCard(
    roles: List<AdminSettingsRole>,
    totalCount: Int,
    listPaging: AdminListPaging,
    loading: Boolean,
    query: String,
    onQueryChange: (String) -> Unit,
    compact: Boolean,
    onCreate: () -> Unit,
    onView: (AdminSettingsRole) -> Unit,
    onEdit: (AdminSettingsRole) -> Unit,
    onDelete: (AdminSettingsRole) -> Unit,
) {
    Column(
        Modifier
            .weight(1f)
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        if (compact) {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text("Roles y permisos", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                RoleSearchField(query, onQueryChange)
                Button(
                    onClick = onCreate,
                    modifier = Modifier.fillMaxWidth().height(44.dp),
                    shape = RoundedCornerShape(10.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text("+ Nuevo rol", fontWeight = FontWeight.SemiBold)
                }
            }
        } else {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.End,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Box(Modifier.widthIn(max = 220.dp)) { RoleSearchField(query, onQueryChange) }
                    Button(
                        onClick = onCreate,
                        shape = RoundedCornerShape(10.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) {
                        Text("+ Nuevo rol", fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        }
        Spacer(Modifier.height(14.dp))
        when {
            loading -> Box(Modifier.weight(1f).fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AdminColors.Primary)
            }
            totalCount == 0 -> Box(Modifier.weight(1f).fillMaxWidth()) {
                Text("No hay roles todavía.", color = AdminColors.TextMuted)
            }
            compact -> Column(Modifier.weight(1f).fillMaxWidth()) {
                VerticalScrollColumn(
                    Modifier.weight(1f).fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    roles.forEach { r -> RoleMobileCard(r, onView, onEdit, onDelete) }
                }
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
            else -> Column(Modifier.weight(1f).fillMaxWidth()) {
                RolesTable(
                    roles,
                    listPaging = listPaging,
                    onView,
                    onEdit,
                    onDelete,
                    modifier = Modifier.weight(1f).fillMaxWidth(),
                )
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
        }
    }
}

@Composable
private fun RoleSearchField(query: String, onQueryChange: (String) -> Unit) {
    OutlinedTextField(
        value = query,
        onValueChange = onQueryChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        placeholder = { Text("Buscar...", color = AdminColors.TextMuted) },
        leadingIcon = { Icon(Icons.Outlined.Search, null, tint = AdminColors.TextMuted) },
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}

private fun rolePermissionSummary(role: AdminSettingsRole): String =
    AdminPermissionCapabilityGroup.entries.mapNotNull { g ->
        val actions = g.actions().filter { a -> g.permission(a)?.let { role.has(it) } == true }
        if (actions.isEmpty()) null
        else "${g.labelEs()}: ${actions.joinToString("/") { it.labelEs() }}"
    }.joinToString(" · ").ifBlank { "Sin permisos" }

@Composable
private fun RolesTable(
    roles: List<AdminSettingsRole>,
    listPaging: AdminListPaging,
    onView: (AdminSettingsRole) -> Unit,
    onEdit: (AdminSettingsRole) -> Unit,
    onDelete: (AdminSettingsRole) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier.fillMaxWidth().fillMaxHeight()) {
        Row(
            Modifier.fillMaxWidth().padding(vertical = 8.dp, horizontal = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(4.dp),
        ) {
            AdminSortableHeader("Rol", "name", listPaging.sort, listPaging::onSort, Modifier.weight(1.2f))
            AdminListHeaderLabel("Resumen", Modifier.weight(2f))
            AdminListHeaderLabel("Acciones", Modifier.width(140.dp))
        }
        HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
        VerticalScrollColumn(Modifier.weight(1f).fillMaxWidth()) {
            roles.forEach { role ->
                Row(
                    Modifier.fillMaxWidth().padding(vertical = 8.dp, horizontal = 4.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    Text(role.name, Modifier.weight(1.2f), fontWeight = FontWeight.SemiBold, color = AdminColors.Text, maxLines = 1)
                    Text(rolePermissionSummary(role), Modifier.weight(2f), color = AdminColors.TextMuted, fontSize = 12.sp, maxLines = 2)
                    Row(Modifier.width(140.dp), horizontalArrangement = Arrangement.spacedBy(2.dp)) {
                        IconButton(onClick = { onView(role) }) {
                            Icon(Icons.Outlined.Search, "Ver", tint = AdminColors.TextMuted)
                        }
                        IconButton(onClick = { onEdit(role) }) {
                            Icon(Icons.Outlined.Edit, "Editar", tint = AdminColors.TextMuted)
                        }
                        IconButton(onClick = { onDelete(role) }) {
                            Icon(Icons.Outlined.Delete, "Eliminar", tint = AdminColors.Error)
                        }
                    }
                }
                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.35f))
            }
        }
    }
}

@Composable
private fun RoleMobileCard(
    item: AdminSettingsRole,
    onView: (AdminSettingsRole) -> Unit,
    onEdit: (AdminSettingsRole) -> Unit,
    onDelete: (AdminSettingsRole) -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.6f), RoundedCornerShape(12.dp))
            .padding(12.dp),
    ) {
        Text(item.name, fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
        Spacer(Modifier.height(6.dp))
        Text(rolePermissionSummary(item), color = AdminColors.TextMuted, fontSize = 12.sp)
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
            IconButton(onClick = { onView(item) }) {
                Icon(Icons.Outlined.Search, "Ver", tint = AdminColors.TextMuted)
            }
            IconButton(onClick = { onEdit(item) }) {
                Icon(Icons.Outlined.Edit, "Editar", tint = AdminColors.TextMuted)
            }
            IconButton(onClick = { onDelete(item) }) {
                Icon(Icons.Outlined.Delete, "Eliminar", tint = AdminColors.Error)
            }
        }
    }
}

@Composable
private fun RoleDetailDialog(item: AdminSettingsRole, onDismiss: () -> Unit) {
    Dialog(onDismissRequest = onDismiss) {
        VerticalScrollColumn(
            Modifier
                .widthIn(max = 640.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text("Detalle del rol", fontWeight = FontWeight.Bold, fontSize = 18.sp)
                IconButton(onClick = onDismiss) {
                    Icon(Icons.Outlined.Close, "Cerrar", tint = AdminColors.TextMuted)
                }
            }
            Spacer(Modifier.height(12.dp))
            Text("Nombre", color = AdminColors.TextMuted, fontSize = 12.sp)
            Text(item.name, fontWeight = FontWeight.Medium, fontSize = 14.sp)
            Spacer(Modifier.height(12.dp))
            PermissionCapabilityMatrix(selected = item.permissions, enabled = false, onToggle = { _, _ -> })
            Spacer(Modifier.height(12.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                Button(
                    onClick = onDismiss,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) { Text("Cerrar") }
            }
        }
    }
}

@Composable
private fun RoleFormDialog(
    title: String,
    initial: AdminSettingsRole?,
    saving: Boolean,
    serverError: String? = null,
    onDismiss: () -> Unit,
    onSave: (AdminSettingsRoleUpsert) -> Unit,
) {
    var name by remember(initial) { mutableStateOf(initial?.name.orEmpty()) }
    var selected by remember(initial) { mutableStateOf(initial?.permissions ?: emptySet()) }
    var formError by remember { mutableStateOf<String?>(null) }

    Dialog(onDismissRequest = { if (!saving) onDismiss() }) {
        VerticalScrollColumn(
            Modifier
                .widthIn(max = 680.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(title, fontWeight = FontWeight.Bold, fontSize = 18.sp)
                IconButton(onClick = onDismiss, enabled = !saving) {
                    Icon(Icons.Outlined.Close, "Cerrar", tint = AdminColors.TextMuted)
                }
            }
            Spacer(Modifier.height(12.dp))
            Text("Nombre del rol *", color = AdminColors.TextMuted, fontSize = 12.sp)
            Spacer(Modifier.height(4.dp))
            OutlinedTextField(
                value = name,
                onValueChange = { name = it },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                shape = RoundedCornerShape(10.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = AdminColors.Primary,
                    unfocusedBorderColor = AdminColors.Outline,
                ),
            )
            Spacer(Modifier.height(12.dp))
            Text("Plantilla rápida", color = AdminColors.TextMuted, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
            Spacer(Modifier.height(6.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                AdminRolePermissionTemplate.entries.forEach { template ->
                    OutlinedButton(
                        onClick = {
                            selected = template.permissions()
                            if (name.isBlank()) name = template.labelEs()
                        },
                        enabled = !saving,
                        shape = RoundedCornerShape(10.dp),
                    ) {
                        Text(template.labelEs(), color = AdminColors.Primary, fontSize = 12.sp)
                    }
                }
            }
            Spacer(Modifier.height(14.dp))
            Text("Permisos por módulo", color = AdminColors.TextMuted, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
            Spacer(Modifier.height(8.dp))
            PermissionCapabilityMatrix(
                selected = selected,
                enabled = !saving,
                onToggle = { perm, checked ->
                    selected = if (checked) selected + perm else selected - perm
                },
            )
            formError?.let {
                Spacer(Modifier.height(8.dp))
                Text(it, color = AdminColors.Error, fontSize = 13.sp)
            }
            serverError?.let {
                Spacer(Modifier.height(8.dp))
                Text(it, color = AdminColors.Error, fontSize = 13.sp)
            }
            Spacer(Modifier.height(16.dp))
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(10.dp, Alignment.End),
            ) {
                OutlinedButton(onClick = onDismiss, enabled = !saving, shape = RoundedCornerShape(10.dp)) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
                Button(
                    onClick = {
                        formError = null
                        if (name.trim().isEmpty()) {
                            formError = "El nombre del rol es obligatorio."
                            return@Button
                        }
                        onSave(AdminSettingsRoleUpsert(name = name.trim(), permissions = selected))
                    },
                    enabled = !saving,
                    shape = RoundedCornerShape(10.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else "Guardar", fontWeight = FontWeight.SemiBold)
                }
            }
        }
    }
}

@Composable
private fun PermissionCapabilityMatrix(
    selected: Set<AdminSettingsPermissionModule>,
    enabled: Boolean,
    onToggle: (AdminSettingsPermissionModule, Boolean) -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.5f), RoundedCornerShape(12.dp))
            .padding(10.dp),
        verticalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            AdminListHeaderLabel("Módulo", Modifier.width(150.dp))
            AdminPermissionAction.entries.forEach { action ->
                AdminListHeaderLabel(action.labelEs(), Modifier.weight(1f))
            }
        }
        HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.4f))
        AdminPermissionCapabilityGroup.entries.forEach { group ->
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                Column(Modifier.width(150.dp)) {
                    Text(group.labelEs(), fontSize = 13.sp, color = AdminColors.Text)
                    // Membresías necesita ver clientes para elegir socios. Solo avisa; no otorga el permiso.
                    val membresiaSinClientes = group == AdminPermissionCapabilityGroup.MEMBERSHIPS &&
                        (selected.contains(AdminSettingsPermissionModule.MEMBERSHIPS_OPERATE) ||
                            selected.contains(AdminSettingsPermissionModule.MEMBERSHIPS_APPROVE)) &&
                        !selected.contains(AdminSettingsPermissionModule.CLIENT_VIEW)
                    if (membresiaSinClientes) {
                        Text(
                            "Necesita Clientes · Ver para elegir socios",
                            fontSize = 11.sp,
                            color = Color(0xFFE65100),
                        )
                    }
                }
                AdminPermissionAction.entries.forEach { action ->
                    val perm = group.permission(action)
                    Box(Modifier.weight(1f), contentAlignment = Alignment.CenterStart) {
                        if (perm == null || action !in group.actions()) {
                            Text("—", color = AdminColors.TextMuted, fontSize = 12.sp)
                        } else {
                            Checkbox(
                                checked = selected.contains(perm),
                                onCheckedChange = { checked -> if (enabled) onToggle(perm, checked) },
                                enabled = enabled,
                                colors = CheckboxDefaults.colors(
                                    checkedColor = AdminColors.Primary,
                                    uncheckedColor = AdminColors.Outline,
                                    disabledCheckedColor = AdminColors.Primary,
                                    disabledUncheckedColor = AdminColors.Outline,
                                ),
                            )
                        }
                    }
                }
            }
        }
    }
}
